import { FormEvent, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { ApiError } from "../api/http";
import { fetchMe, getStoredSession, loginWithCode, logoutLocally, sendLoginCode } from "../api/auth";
import { AuthSession } from "../api/tokenStore";
import { bindTeacherLink, getTeacherLink, getTeacherLinkBinding, TeacherLinkInfo, TeacherStudentBinding } from "../api/teacherWeb";
import { VideoUploadWorkflow } from "../features/upload/VideoUploadWorkflow";

type PageStatus = "loading" | "ready" | "error";
type BindingStatus = "idle" | "loading" | "ready" | "error";
type LoginStatus = "idle" | "sendingCode" | "loggingIn" | "binding";

const lastTeacherTokenKey = "pingpi.teacherWeb.lastTeacherToken";

function humanError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "操作失败，请稍后重试";
}

function isPhoneInputValid(phone: string): boolean {
  return /^\d{11}$/.test(phone.trim());
}

function isCodeInputValid(code: string): boolean {
  return /^\d{6}$/.test(code.trim());
}

function isNameInputValid(name: string): boolean {
  const normalized = name.trim();
  return normalized.length > 0 && normalized.length <= 64;
}

function normalizeDigits(value: string, maxLength: number): string {
  return value.replace(/\D/g, "").slice(0, maxLength);
}

function StudentLoginBrand() {
  return (
    <div className="student-login-header">
      <div className="student-login-brand" aria-label="迹时运动">
        <img src="/pingpi-logo.png" alt="" aria-hidden="true" />
        <span>迹时运动</span>
      </div>
      <span className="student-login-mark">AI教练</span>
    </div>
  );
}

export function TeacherEntryPage() {
  const params = useParams<{ token: string }>();
  const token = params.token?.trim();

  const [status, setStatus] = useState<PageStatus>("loading");
  const [link, setLink] = useState<TeacherLinkInfo | null>(null);
  const [linkError, setLinkError] = useState("");
  const [session, setSession] = useState<AuthSession | null>(() => getStoredSession());
  const [binding, setBinding] = useState<TeacherStudentBinding | null>(null);
  const [bindingStatus, setBindingStatus] = useState<BindingStatus>("idle");
  const [requiresStudentName, setRequiresStudentName] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [studentName, setStudentName] = useState("");
  const [formError, setFormError] = useState("");
  const [loginStatus, setLoginStatus] = useState<LoginStatus>("idle");
  const [cooldown, setCooldown] = useState(0);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const currentUser = session?.user ?? null;
  const classTitle = link?.title || "AI教练课程";
  const teacherName = link?.teacher.teacherName.trim() || "";
  const teacherLabel = teacherName ? (teacherName.endsWith("老师") ? teacherName : `${teacherName}老师`) : "老师";
  const canSendCode = isPhoneInputValid(phone) && loginStatus === "idle" && cooldown <= 0;
  const canSubmitLogin = isPhoneInputValid(phone) && isCodeInputValid(code) && loginStatus === "idle";
  const hasNamedBinding = Boolean(binding?.studentName?.trim());
  const isAuthedEntry = status === "ready" && Boolean(link && currentUser);
  const showBindingLoading = isAuthedEntry && (bindingStatus === "idle" || bindingStatus === "loading");
  const showNameStep = status === "ready" && Boolean(link && currentUser) && bindingStatus === "ready" && requiresStudentName;
  const showUploadStep = status === "ready" && Boolean(link && currentUser) && bindingStatus === "ready" && hasNamedBinding;
  const pageClassName = !currentUser || showBindingLoading || showNameStep ? "student-login-page" : "entry-page";

  const buttonText = useMemo(() => {
    if (loginStatus === "sendingCode") return "发送中";
    if (loginStatus === "loggingIn") return "登录中";
    if (loginStatus === "binding") return "绑定中";
    return null;
  }, [loginStatus]);

  useEffect(() => {
    if (!token) {
      return;
    }

    try {
      window.localStorage.setItem(lastTeacherTokenKey, token);
    } catch {
      // Ignore private-mode storage failures; the link page itself still works.
    }

    const controller = new AbortController();
    setStatus("loading");
    setLinkError("");

    getTeacherLink(token, controller.signal)
      .then((info) => {
        setLink(info);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setLinkError(humanError(error));
        setStatus("error");
      });

    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setCooldown((value) => Math.max(0, value - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (!session) {
      return;
    }

    fetchMe().catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 401) {
        logoutLocally();
        setSession(null);
      }
    });
  }, [session]);

  useEffect(() => {
    if (!token || !currentUser || status !== "ready") {
      setBinding(null);
      setBindingStatus("idle");
      setRequiresStudentName(false);
      return;
    }

    const controller = new AbortController();
    setBindingStatus("loading");
    setFormError("");

    getTeacherLinkBinding(token, controller.signal)
      .then((result) => {
        setBinding(result.binding);
        setRequiresStudentName(result.requiresStudentName);
        setStudentName(result.binding?.studentName ?? "");
        setBindingStatus("ready");
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setBinding(null);
        setRequiresStudentName(false);
        setFormError(humanError(error));
        setBindingStatus("error");
      });

    return () => controller.abort();
  }, [currentUser, status, token]);

  if (!token) {
    return <Navigate to="/compat" replace />;
  }

  async function handleSendCode() {
    if (!isPhoneInputValid(phone)) {
      setFormError("请输入 11 位手机号");
      return;
    }

    setFormError("");
    setLoginStatus("sendingCode");

    try {
      const response = await sendLoginCode(phone.trim());
      setCooldown(response.cooldownSec || 60);
    } catch (error) {
      const message = humanError(error);
      setFormError(error instanceof ApiError && error.retryAfterSec ? `${message}，${error.retryAfterSec} 秒后再试` : message);
    } finally {
      setLoginStatus("idle");
    }
  }

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!isPhoneInputValid(phone)) {
      setFormError("请输入 11 位手机号");
      return;
    }

    if (!isCodeInputValid(code)) {
      setFormError("请输入 6 位验证码");
      return;
    }

    setFormError("");
    setLoginStatus("loggingIn");

    try {
      const nextSession = await loginWithCode(phone.trim(), code.trim());
      setSession(nextSession);
    } catch (error) {
      setFormError(humanError(error));
    } finally {
      setLoginStatus("idle");
    }
  }

  async function handleBind(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!token || !session) {
      setFormError("请先登录");
      return;
    }

    if (!isNameInputValid(studentName)) {
      setFormError("请输入学生姓名，最多 64 个字符");
      return;
    }

    setFormError("");
    setLoginStatus("binding");

    try {
      const result = await bindTeacherLink(token, studentName.trim());
      setBinding(result);
      setRequiresStudentName(false);
      setBindingStatus("ready");
    } catch (error) {
      setFormError(humanError(error));
    } finally {
      setLoginStatus("idle");
    }
  }

  function handleLogoutClick() {
    setShowLogoutConfirm(true);
  }

  function handleLogoutConfirm() {
    setShowLogoutConfirm(false);
    logoutLocally();
    setSession(null);
    setBinding(null);
    setBindingStatus("idle");
    setRequiresStudentName(false);
    setStudentName("");
    setPhone("");
    setCode("");
    setCooldown(0);
    setFormError("");
    setLoginStatus("idle");
  }

  function handleLogoutCancel() {
    setShowLogoutConfirm(false);
  }

  return (
    <div className={pageClassName}>
      {status === "loading" ? (
        <section className="student-login-card">
          <StudentLoginBrand />
          <div className="empty-state">正在读取老师链接</div>
        </section>
      ) : null}

      {status === "error" ? (
        <section className="student-login-card">
          <StudentLoginBrand />
          <div className="empty-state">{linkError || "老师链接不可用"}</div>
        </section>
      ) : null}

      {status === "ready" && link && !currentUser ? (
        <section className="student-login-card">
          <StudentLoginBrand />
          <div className="student-login-copy">
            <h2>{classTitle}</h2>
            <p>{teacherLabel}邀请你加入。</p>
          </div>

          <form className="student-login-form" onSubmit={handleLogin}>
            <label className="student-login-field" htmlFor="phone">
              <span className="student-login-prefix">+86</span>
              <input
                id="phone"
                name="phone"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="请输入手机号"
                value={phone}
                onChange={(event) => setPhone(normalizeDigits(event.target.value, 11))}
              />
            </label>

            <label className="student-login-field" htmlFor="code">
              <input
                id="code"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="请输入验证码"
                value={code}
                onChange={(event) => setCode(normalizeDigits(event.target.value, 6))}
              />
              <button
                className="student-login-code"
                type="button"
                disabled={!canSendCode}
                onClick={handleSendCode}
              >
                {loginStatus === "sendingCode" ? "发送中" : cooldown > 0 ? `${cooldown}s` : "获取验证码"}
              </button>
            </label>

            {formError ? <p className="form-error">{formError}</p> : null}
            <button className="primary-button student-login-submit" type="submit" disabled={!canSubmitLogin}>
              {buttonText ?? "登录"}
            </button>
          </form>
        </section>
      ) : null}

      {showBindingLoading ? (
        <section className="student-login-card">
          <StudentLoginBrand />
          <div className="empty-state">正在进入课堂</div>
        </section>
      ) : null}

      {status === "ready" && link && currentUser && bindingStatus === "error" ? (
        <section className="student-login-card">
          <StudentLoginBrand />
          <div className="empty-state">{formError || "课堂信息读取失败"}</div>
        </section>
      ) : null}

      {showNameStep ? (
        <section className="student-login-card student-name-card">
          <StudentLoginBrand />
          <div className="student-login-copy student-name-copy">
            <h2>填写真实姓名</h2>
            <p>{classTitle}</p>
          </div>

          <form className="student-login-form student-name-form" onSubmit={handleBind}>
            <label className="student-login-field" htmlFor="studentName">
              <input
                id="studentName"
                name="studentName"
                autoComplete="name"
                placeholder="请输入真实姓名"
                value={studentName}
                maxLength={64}
                onChange={(event) => setStudentName(event.target.value)}
              />
            </label>
            <p className="student-name-note">请填写真实姓名，若后续需要更改姓名，请联系老师。</p>
            {formError ? <p className="form-error">{formError}</p> : null}
            <button className="primary-button student-login-submit" type="submit" disabled={!isNameInputValid(studentName) || loginStatus !== "idle"}>
              {buttonText ?? "确定"}
            </button>
          </form>
          <button className="student-name-logout" type="button" aria-label="退出登录" title="退出登录" onClick={handleLogoutClick}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 6.75V5.5A2.5 2.5 0 0 1 11.5 3h5A2.5 2.5 0 0 1 19 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-5A2.5 2.5 0 0 1 9 18.5v-1.25" />
              <path d="M4 12h10" />
              <path d="m11 8 4 4-4 4" />
            </svg>
          </button>
        </section>
      ) : null}

      {showUploadStep && binding && link ? (
        <div className="entry-upload-flow">
          <VideoUploadWorkflow binding={binding} link={link} token={token} onLogout={handleLogoutClick} />
        </div>
      ) : null}

      {showLogoutConfirm ? (
        <div className="student-logout-dialog-backdrop" role="presentation" onClick={handleLogoutCancel}>
          <div
            aria-labelledby="student-logout-title"
            aria-modal="true"
            className="student-logout-dialog"
            role="dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div>
              <h3 id="student-logout-title">退出登录？</h3>
              <p>退出后需要重新输入手机号和验证码。</p>
            </div>
            <div className="student-logout-actions">
              <button className="student-logout-secondary" type="button" onClick={handleLogoutCancel}>
                取消
              </button>
              <button className="student-logout-primary" type="button" onClick={handleLogoutConfirm}>
                退出登录
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
